/**
 * Season result captured at player signup (final objective / expected result).
 * Do not fall back to training axes, quest titles, phase explanations, or Pingflo copy.
 */

/** Signup objective kinds with `smartFinalObjectiveKind_*` i18n keys. */
export const SIGNUP_OBJECTIVE_KINDS = [
  "classement",
  "team_championship",
  "tournament",
  "other",
];

/** English fallbacks when no `t` is passed (tests / non-UI). */
const KIND_LABELS_EN = {
  classement: "Climb ranking",
  team_championship: "Win / promote in the team championship",
  tournament: "Win a tournament",
  other: "Other",
  classement_step: "Ranking progression",
  ranking_points: "Points target",
  match_target: "Win in competition",
  category: "Category placement",
};

/**
 * Known signup chip texts across supported UI languages.
 * Used to recover a kind when only a free-text label was stored.
 */
const KNOWN_LABEL_TO_KIND = (() => {
  const pairs = [
    ["classement", "Climb ranking"],
    ["classement", "Monter de classement"],
    ["classement", "Im Ranking aufsteigen"],
    ["classement", "Subir de clasificación"],
    ["classement", "Stijgen in ranking"],
    ["classement", "Subir de classificação"],
    ["team_championship", "Win / promote in the team championship"],
    ["team_championship", "Gagner/monter division au championnat par équipes"],
    ["team_championship", "Gagner / monter division au championnat par équipes"],
    ["team_championship", "Im Mannschaftswettbewerb gewinnen / aufsteigen"],
    ["team_championship", "Ganar / subir de división en el campeonato por equipos"],
    ["team_championship", "Winnen / promoveren in het teamkampioenschap"],
    ["team_championship", "Ganhar / subir de divisão no campeonato por equipas"],
    ["tournament", "Win a tournament"],
    ["tournament", "Gagner un tournoi"],
    ["tournament", "Ein Turnier gewinnen"],
    ["tournament", "Ganar un torneo"],
    ["tournament", "Een toernooi winnen"],
    ["tournament", "Ganhar um torneio"],
  ];
  const map = new Map();
  for (const [kind, label] of pairs) {
    map.set(normalizeLabelKey(label), kind);
  }
  return map;
})();

function normalizeLabelKey(label) {
  return String(label || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Pingflo phase / SCforH explanation text — never show as Expected Results. */
export function isSignupSeasonResultNoise(label) {
  const lab = String(label || "").trim();
  if (!lab) return true;
  if (/^by mid-season,/i.test(lab)) return true;
  if (/d[’']ici la mi[- ]saison/i.test(lab)) return true;
  if (/de aqu[ií] a mitad de temporada/i.test(lab)) return true;
  if (/bis zur saisonmitte/i.test(lab)) return true;
  if (/pingflo/i.test(lab)) return true;
  if (/digiping/i.test(lab)) return true;
  if (/\bOMS\b|\bWHO\b/i.test(lab) && /rappel|reminder|quota|suivi|follow-?up/i.test(lab)) {
    return true;
  }
  if (/^Progress on /i.test(lab)) return true;
  if (/^Review SMART/i.test(lab)) return true;
  // Presence defaults belong on letter A, not Expected Results (S).
  if (/train most weeks at least two sessions/i.test(lab)) return true;
  if (/au moins deux séances/i.test(lab)) return true;
  if (/come to the club most weeks/i.test(lab)) return true;
  if (/Keep \d+ sessions \/ week for health/i.test(lab)) return true;
  if (/^Keep \d+ sessions/i.test(lab)) return true;
  // Long composed sentences are explanations, not the signup chip.
  if (lab.length > 120 && /[,;—–-]/.test(lab)) return true;
  return false;
}

/** True when label is a known signup expected-result chip (any language). */
export function isKnownSignupSeasonResultLabel(label) {
  const key = normalizeLabelKey(label);
  return Boolean(key && KNOWN_LABEL_TO_KIND.has(key));
}

function cleanLabel(value) {
  const lab = String(value || "").trim();
  if (!lab || isSignupSeasonResultNoise(lab)) return null;
  return lab;
}

function kindsFromObj(obj) {
  if (!obj || typeof obj !== "object") return [];
  if (Array.isArray(obj.kinds) && obj.kinds.length) {
    return obj.kinds.map((id) => String(id || "").trim()).filter(Boolean);
  }
  const one = String(obj.kind || "").trim();
  return one ? [one] : [];
}

function inferKindFromLabel(label) {
  const key = normalizeLabelKey(label);
  return key ? KNOWN_LABEL_TO_KIND.get(key) || null : null;
}

function isSignupObjectiveKind(id) {
  return SIGNUP_OBJECTIVE_KINDS.includes(String(id || "").trim());
}

function localizeKind(kind, obj, t) {
  const id = String(kind || "").trim();
  if (!id || !isSignupObjectiveKind(id)) return null;
  if (id === "other") {
    return cleanLabel(obj?.customText || obj?.label) || null;
  }
  if (typeof t === "function") {
    const translated = String(
      t(`smartFinalObjectiveKind_${id}`, {
        defaultValue: KIND_LABELS_EN[id] || "",
      }) || ""
    ).trim();
    if (translated) return translated;
  }
  return KIND_LABELS_EN[id] || null;
}

function labelFromKinds(obj, t) {
  const kinds = kindsFromObj(obj);
  if (!kinds.length) return null;
  // Only localize signup chips; mid-season kinds (ranking_points, …) keep free text.
  if (!kinds.every(isSignupObjectiveKind)) return null;
  const bits = kinds.map((id) => localizeKind(id, obj, t)).filter(Boolean);
  return bits.length ? bits.join(" · ") : null;
}

/**
 * Resolve the signup Expected Results label.
 * Prefer kind → i18n when `t` is provided so the UI language wins over the
 * language used at signup.
 */
export function resolveSignupSeasonResult(
  seasonQuest,
  seasonHorizon = null,
  slotsA = null,
  t = null
) {
  const sq = seasonQuest && typeof seasonQuest === "object" ? seasonQuest : {};
  const sh = seasonHorizon && typeof seasonHorizon === "object" ? seasonHorizon : {};
  const a = slotsA && typeof slotsA === "object" ? slotsA : {};
  const finalObj = sq.finalObjective || a.finalObjective || null;

  // 1) Kind-first localization (signup chips).
  const fromKind =
    labelFromKinds(finalObj, t) ||
    labelFromKinds(sq.expectedResultDraft, t) ||
    labelFromKinds(sq.expectedResult, t) ||
    labelFromKinds(a.seasonQuest?.expectedResultDraft, t) ||
    labelFromKinds(a.seasonQuest?.expectedResult, t);
  if (fromKind) return fromKind;

  // 2) Free-text candidates — if they match a known chip, re-localize by kind.
  const candidates = [
    finalObj?.label,
    sh.finalObjectiveLabel,
    sq.expectedResult?.label,
    sq.expectedResultDraft?.label,
    a.seasonQuest?.expectedResult?.label,
    a.seasonQuest?.expectedResultDraft?.label,
    a.labels?.finalObjective,
    sh.expectedResultLabel,
  ];
  for (const c of candidates) {
    const lab = cleanLabel(c);
    if (!lab) continue;
    const inferred = inferKindFromLabel(lab);
    if (inferred) {
      const localized = localizeKind(inferred, { kind: inferred, label: lab }, t);
      if (localized) return localized;
    }
    return lab;
  }

  return null;
}

/** Primary + secondary technical training axes for the season (not results). */
export function resolveTrainingAxes(seasonQuest, seasonHorizon = null, goals = []) {
  const sq = seasonQuest && typeof seasonQuest === "object" ? seasonQuest : {};
  const sh = seasonHorizon && typeof seasonHorizon === "object" ? seasonHorizon : {};
  const axes = [];

  const primary =
    String(sq.trainingAxes?.primary || sq.primaryObjective?.label || sh.primaryGoalLabel || "").trim() ||
    null;
  if (primary) axes.push({ label: primary, role: "primary" });

  const secondaries = Array.isArray(sq.trainingAxes?.secondaries)
    ? sq.trainingAxes.secondaries
    : [];
  for (const s of secondaries) {
    const label = String(s || "").trim();
    if (!label || label === primary) continue;
    axes.push({ label, role: "secondary" });
  }

  if (!axes.length && Array.isArray(goals)) {
    for (const g of goals) {
      const label = String(g?.label || "").trim();
      if (!label) continue;
      axes.push({
        label,
        role: g.is_primary ? "primary" : "secondary",
      });
    }
  }

  return axes;
}
